import { css, cva } from '@repo/panda/css';
import type { SpacingToken, SystemStyleObject } from '@repo/panda/types';
import type React from 'react';

const flexRowStyles = cva({
    base: {
        display: 'flex',
        flexDirection: 'row',
    },
    variants: {
        justify: {
            start: { justifyContent: 'flex-start' },
            end: { justifyContent: 'flex-end' },
            center: { justifyContent: 'center' },
            between: { justifyContent: 'space-between' },
            around: { justifyContent: 'space-around' },
            evenly: { justifyContent: 'space-evenly' },
        },
        align: {
            start: { alignItems: 'flex-start' },
            end: { alignItems: 'flex-end' },
            center: { alignItems: 'center' },
            baseline: { alignItems: 'baseline' },
            stretch: { alignItems: 'stretch' },
        },
        wrap: {
            nowrap: { flexWrap: 'nowrap' },
            wrap: { flexWrap: 'wrap' },
            'wrap-reverse': { flexWrap: 'wrap-reverse' },
        },
        width: {
            auto: { width: 'auto' },
            full: { width: 'full' },
        },
        height: {
            auto: { height: 'auto' },
            full: { height: 'full' },
        },
    },
    defaultVariants: {
        justify: 'start',
        align: 'start',
        wrap: 'nowrap',
        width: 'auto',
        height: 'auto',
    },
});

export type FlexRowVariants = NonNullable<Parameters<typeof flexRowStyles>[0]>;

export interface FlexRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
    justify?: FlexRowVariants['justify'];
    align?: FlexRowVariants['align'];
    gap?: SpacingToken;
    wrap?: FlexRowVariants['wrap'];
    width?: FlexRowVariants['width'];
    height?: FlexRowVariants['height'];
    children?: React.ReactNode;
    className?: SystemStyleObject; // Pass `css.raw({})` from components, not a class string.
}

export const FlexRow: React.FC<FlexRowProps> = ({
    justify,
    align,
    gap,
    wrap,
    width,
    height,
    children,
    className,
    ...props
}: FlexRowProps) => {
    const flexRowClassName = flexRowStyles.raw({ justify, align, wrap, width, height });

    return (
        <div className={css(flexRowClassName, className, css.raw({ gap }))} {...props}>
            {children}
        </div>
    );
};
